import { useEffect, useState } from "react";
import { CheckCircle2, Send, ShieldAlert, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Iter 59 Task 1 ,  Social post approval gate.
 *
 * Renders when opportunity has social_post_ready=true. Shows the platform
 * (Acquigine) post draft AND the subscriber's (BYOK) post draft side by
 * side, with an explicit Approve & Post button. Nothing publishes until
 * the button is clicked.
 */
export default function SocialPostApprovalPanel({ opp, onUpdate }) {
  const [preview, setPreview] = useState(null);
  const [approving, setApproving] = useState(false);

  const load = async () => {
    if (!opp?.oid) return;
    try {
      const r = await api.get(`/opportunities/${opp.oid}/social-post-preview`);
      setPreview(r.data);
    } catch {
      // preview endpoint absent or opp not ready ,  hide panel
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [opp?.oid, opp?.social_post_ready]);

  if (!preview) return null;
  if (!preview.social_post_ready && !preview.social_post_approved_by) return null;

  const drafts = preview.drafts || {};
  const platforms = Object.keys(drafts);
  const alreadyApproved = !!preview.social_post_approved_by;

  const approve = async () => {
    if (!window.confirm(
      "This will publicly post to Acquigine's channels and your connected social accounts. This action is IRREVERSIBLE. Continue?"
    )) return;
    setApproving(true);
    try {
      const r = await api.post(`/opportunities/${opp.oid}/approve-social-post`);
      toast.success("Social posts approved and dispatched");
      if (onUpdate) onUpdate();
      load();
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setApproving(false);
    }
  };

  return (
    <div
      className="ae-card p-6 border-emerald-500/30"
      data-testid="social-post-approval-panel"
    >
      <div className="flex items-start gap-3 mb-4">
        <Share2 className="h-5 w-5 text-emerald-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Ready for social ,  approval required</div>
          <h3 className="font-heading text-xl tracking-tight">Preview both posts before they go live</h3>
          <p className="text-xs text-slate-400 mt-1">
            This deal has reached <b>Contract Signed</b>. Nothing has been
            posted yet. Review both drafts, then explicitly approve to
            publish. If not approved within 48 hours, you&apos;ll receive a
            single email reminder &mdash; we never auto-post.
          </p>
        </div>
      </div>

      {alreadyApproved && (
        <div className="text-xs text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded-sm p-3 mb-3 inline-flex items-center gap-2" data-testid="social-post-approved-badge">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Approved {preview.social_post_approved_at ? `at ${preview.social_post_approved_at}` : ""}. Publication in progress.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <PreviewCard
          testid="platform-post-preview"
          title="Acquigine platform post"
          subtitle="Promoted on Acquigine's own channels"
          drafts={drafts}
          platforms={platforms}
          willPublish={preview.platform_post?.will_publish}
          note={preview.platform_post?.note}
        />
        <PreviewCard
          testid="subscriber-post-preview"
          title="Your post (BYOK)"
          subtitle="Published to YOUR connected accounts"
          drafts={drafts}
          platforms={platforms}
          willPublish={preview.subscriber_post?.will_publish}
          note={preview.subscriber_post?.note}
        />
      </div>

      {!alreadyApproved && (
        <div className="flex items-center gap-3">
          <button
            data-testid="approve-social-post-btn"
            onClick={approve}
            disabled={approving || platforms.length === 0}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-xs font-semibold px-4 py-2 rounded-sm"
          >
            <Send className="h-3.5 w-3.5" />
            {approving ? "Approving..." : "Approve & post now"}
          </button>
          <span className="text-[11px] text-slate-500 inline-flex items-center gap-1.5">
            <ShieldAlert className="h-3 w-3" />
            Public, irreversible action on real social accounts.
          </span>
        </div>
      )}
    </div>
  );
}


function PreviewCard({ testid, title, subtitle, drafts, platforms, willPublish, note }) {
  return (
    <div
      className={`border rounded-sm p-4 ${willPublish ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-700 bg-slate-800/40"}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-mono uppercase text-emerald-300">{title}</div>
          <div className="text-[11px] text-slate-500">{subtitle}</div>
        </div>
        {willPublish ? (
          <span className="text-[10px] px-1.5 py-0.5 border border-emerald-500/40 text-emerald-300 rounded-sm">Will publish</span>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 border border-slate-600 text-slate-400 rounded-sm" title={note || ""}>
            {note === "not_connected" ? "Not connected" : "Won't publish"}
          </span>
        )}
      </div>
      {platforms.length === 0 && (
        <div className="text-[11px] text-slate-500 italic">No drafts cached yet ,  refresh in a few seconds.</div>
      )}
      <div className="space-y-2">
        {platforms.map((p) => (
          <div key={p} className="border border-white/10 bg-black/30 rounded-sm p-2 text-xs text-slate-200 whitespace-pre-wrap" data-testid={`draft-${testid}-${p}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{p}</div>
            {drafts[p]}
          </div>
        ))}
      </div>
    </div>
  );
}
